import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { fetchTaxonomy, type TaxonomyCategory, type TaxonomyPayload } from '../api/taxonomy'
import type { NeedCategory } from '../types/api'

/**
 * Loads the shared taxonomy once and hands it to every picker in the app.
 *
 * Why this exists: the option strings a learner picks must be byte-identical to the ones a
 * provider picks, or the two can never match, so the server rejects anything outside its own
 * lists. Holding a second copy of those lists in the client is therefore not a caching decision
 * but a correctness bug waiting to happen - and it did happen: the hard-coded lists this replaces
 * offered "Painting", "Coding", "Prompt Engineering" and "English", none of which the server
 * accepts.
 *
 * /api/taxonomy is public, cacheable and needs no token, so this loads before sign-in too.
 */
interface TaxonomyContextValue {
  taxonomy: TaxonomyPayload | null
  loading: boolean
  error: string | null
  /** Every category, in the order the server declared them. */
  categories: TaxonomyCategory[]
  /** One category by the enum name used on the wire, e.g. "SPORTS_FITNESS". */
  categoryOf: (category: NeedCategory) => TaxonomyCategory | undefined
  /** Display label, falling back to the raw name while the payload is still loading. */
  labelOf: (category: NeedCategory) => string
  /** The matchable items for a category, or [] until loaded. */
  itemsOf: (category: NeedCategory) => string[]
  /** Allowed levels, or null where the category is graded by class rather than level. */
  levelsOf: (category: NeedCategory) => string[] | null
  /** Allowed answers for one per-category detail question, e.g. ("ACADEMICS", "board"). */
  detailOptionsOf: (category: NeedCategory, key: string) => string[]
  /** Options shared across every category: mode, timing, budget_band, ... */
  common: TaxonomyPayload['common']
}

const EMPTY_COMMON: TaxonomyPayload['common'] = {
  mode: [],
  timing: [],
  budget_band: [],
  preferred_tutor_gender: [],
  levels: [],
  day: [],
  teaching_language: [],
}

const TaxonomyContext = createContext<TaxonomyContextValue | undefined>(undefined)

export function TaxonomyProvider({ children }: { children: ReactNode }) {
  const [taxonomy, setTaxonomy] = useState<TaxonomyPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchTaxonomy()
      .then((t) => {
        if (!cancelled) setTaxonomy(t)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Could not load the options list.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<TaxonomyContextValue>(() => {
    // Indexed by the wire name, since that is what every form holds in state.
    const byName = new Map<string, TaxonomyCategory>()
    const ordered: TaxonomyCategory[] = []
    if (taxonomy) {
      for (const [label, cat] of Object.entries(taxonomy.categories)) {
        // The payload keys categories by label; carry it onto the object so a caller never has
        // to look in two places for the display name.
        const withLabel = { ...cat, label } as TaxonomyCategory & { label: string }
        byName.set(cat.name, withLabel)
        ordered.push(withLabel)
      }
    }

    const categoryOf = (c: NeedCategory) => byName.get(c)

    return {
      taxonomy,
      loading,
      error,
      categories: ordered,
      categoryOf,
      labelOf: (c) => (categoryOf(c) as (TaxonomyCategory & { label?: string }) | undefined)?.label ?? c,
      itemsOf: (c) => categoryOf(c)?.items ?? [],
      levelsOf: (c) => categoryOf(c)?.levels ?? null,
      detailOptionsOf: (c, key) => categoryOf(c)?.detail?.[key] ?? [],
      common: taxonomy?.common ?? EMPTY_COMMON,
    }
  }, [taxonomy, loading, error])

  return <TaxonomyContext.Provider value={value}>{children}</TaxonomyContext.Provider>
}

export function useTaxonomy(): TaxonomyContextValue {
  const ctx = useContext(TaxonomyContext)
  if (!ctx) throw new Error('useTaxonomy must be used within TaxonomyProvider')
  return ctx
}
